import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Card, Button, Input, Field, ErrorBanner } from '../../components/ui';

export function PortalLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [error, setError] = useState<string | null>(null);

  const requestMut = useMutation({
    mutationFn: () => api.post('/portal/auth/magic-link', { email }),
    onSuccess: () => setStep('code'),
    onError: (e) => setError(e instanceof Error ? e.message : 'Failed'),
  });

  const verifyMut = useMutation({
    mutationFn: () => api.post<{ token: string }>('/portal/auth/verify', { email, code }),
    onSuccess: ({ token }) => {
      localStorage.setItem('frontdesk-portal-token', token);
      navigate('/portal');
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Invalid code'),
  });

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--color-surface-secondary)', padding: 'var(--space-4)' }}>
      <Card style={{ maxWidth: 420, width: '100%' }}>
        <h1 style={{ margin: '0 0 var(--space-2)', fontSize: 'var(--text-2xl)', fontWeight: 700 }}>Client portal</h1>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
          {step === 'email'
            ? "Enter your email and we'll send you a magic code."
            : `Check ${email} for a 6-digit code.`}
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            if (step === 'email') {
              if (!email) return setError('Email required');
              requestMut.mutate();
            } else {
              if (!code) return setError('Code required');
              verifyMut.mutate();
            }
          }}
          style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', marginTop: 'var(--space-3)' }}
        >
          {step === 'email' ? (
            <Field label="Email">
              <Input value={email} onChange={setEmail} type="email" autoFocus />
            </Field>
          ) : (
            <Field label="6-digit code">
              <Input value={code} onChange={setCode} placeholder="123456" autoFocus />
            </Field>
          )}
          <ErrorBanner error={error} />
          <Button type="submit" disabled={requestMut.isPending || verifyMut.isPending}>
            {step === 'email' ? (requestMut.isPending ? 'Sending…' : 'Send code') : verifyMut.isPending ? 'Verifying…' : 'Sign in'}
          </Button>
          {step === 'code' && (
            <Button variant="ghost" onClick={() => setStep('email')}>
              Use different email
            </Button>
          )}
        </form>
      </Card>
    </div>
  );
}
